import {partition} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React, {useState, ComponentType, useLayoutEffect, useMemo, useCallback, useEffect} from 'react';
import {
  ReactFlow,
  Node,
  useNodesInitialized,
  NodeProps,
  ReactFlowProvider,
  applyNodeChanges,
  useEdgesState,
  XYPosition,
  SelectionMode,
} from 'reactflow';

import {assert, assertNotNullAndReturn} from 'Asserts';
import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {UnitOperationFields} from 'studies/typed/shared/unit-op-linking-flowchart/useUnitOpLinkingState';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_SEMIDARK, WHITE} from 'ui-library/Colors';
import DefaultBackground from 'ui-library/reactflow/backgrounds/DefaultBackground';
import HorizontalControls from 'ui-library/reactflow/controls/HorizontalControls';
import {REACT_FLOW_PRO_OPTIONS} from 'ui-library/reactflow/ReactFlowConstants';
import FlowchartContextPanel, {
  PanelSide,
  SidepanelSize,
} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';

import BulkSelectOptionsForMultipleItemsTree, {
  TreeNode,
  TreeNodeType,
} from '../../process-development/BulkSelectOptionsForMultipleItemsTree';
import UnitOperationFlowchartNode, {NodeType} from './UnitOperationFlowchartNode';
import {
  makeUnitOpLinkEdge,
  makeSourcePMEINode,
  makeDestinationPMEINode,
  getOrderedDestPMEIsInColumns,
  makeIntraRunMethodEdges,
  sortComparePMEIs,
  getNodePositionsByPMEIIdForProcedureRun,
  FIRST_DEST_COLUMN_X,
  HIGHEST_DEST_NODE_Y,
  NODE_HORIZONTAL_GAP,
  NODE_VERTICAL_GAP,
  UnitOpLinkEdge,
  SourcePMEINode,
  DestinationPMEINode,
  getHeightOfNodeColumn,
} from './UnitOpLinkingFlowchartUtils';

const STATIC_PROPS: Partial<React.ComponentProps<typeof ReactFlow>> = {
  nodesConnectable: false,
  nodesDraggable: false,
  elementsSelectable: true,
  selectNodesOnDrag: true,
  panOnScroll: true,
  panOnDrag: true,
  selectionKeyCode: 'Shift',
  multiSelectionKeyCode: 'Shift',
  zoomOnScroll: false,
  zoomOnPinch: true,
  defaultViewport: {x: 600, y: 30, zoom: 1},
  proOptions: REACT_FLOW_PRO_OPTIONS,
  selectionMode: SelectionMode.Partial,
};

const NODE_TYPES: Record<NodeType, ComponentType<NodeProps>> = {
  [NodeType.SOURCE_PMEI]: UnitOperationFlowchartNode,
  [NodeType.DESTINATION_PMEI]: UnitOperationFlowchartNode,
};

interface UnitOpLinkingFlowchartEditorProps {
  initialLinks: Array<UnitOpLinkEdge>;
  onChangeLinks: (links: Array<UnitOpLinkEdge>) => void;
  sourcePMEIs: Array<UnitOperationFields>;
  destinationPMEIs: Array<UnitOperationFields>;
}

export default function UnitOpLinkingFlowchartEditor(props: UnitOpLinkingFlowchartEditorProps): JSX.Element {
  return (
    <ReactFlowProvider>
      <ReactFlowWrapper fullWidth={true}>
        <PresentationalUnitOpLinkingFlowchartEditor {...props} />
      </ReactFlowWrapper>
    </ReactFlowProvider>
  );
}

function PresentationalUnitOpLinkingFlowchartEditor(props: UnitOpLinkingFlowchartEditorProps): JSX.Element {
  const orderedDestPMEIsInColumns = useMemo(
    () => getOrderedDestPMEIsInColumns(props.destinationPMEIs),
    [props.destinationPMEIs]
  );
  const intraRunMethodEdges = useMemo(
    () => makeIntraRunMethodEdges(orderedDestPMEIsInColumns),
    [orderedDestPMEIsInColumns]
  );
  const [unitOpLinkEdges, setUnitOpLinkEdges, onUnitOpLinkEdgesChange] = useEdgesState<UnitOpLinkEdge>(
    props.initialLinks.map((data) => makeUnitOpLinkEdge(data))
  );
  const [srcNodes, setSrcNodes] = useState<Array<SourcePMEINode>>(
    makeInitialSourcePMEINodes(props.sourcePMEIs, props.initialLinks)
  );
  const [destNodes, setDestNodes] = useState<Array<DestinationPMEINode>>(
    makeInitialDestinationPMEINodes(orderedDestPMEIsInColumns)
  );
  const areNodesInitialized = useNodesInitialized();
  const [isSelecting, setIsSelecting] = useState(false);

  const selectedDestNodes: Array<DestinationPMEINode> = destNodes.filter((node) => node.selected);

  const [isRightSidepanelOpen, setIsRightSidepanelOpen] = useState(false);

  useEffect(() => {
    setIsRightSidepanelOpen(selectedDestNodes.length > 0);
  }, [selectedDestNodes.length]);

  const handleRightSidepanelClose = useCallback(() => {
    setIsRightSidepanelOpen(false);
    setDestNodes((prevNodes) =>
      prevNodes.map((node) => ({
        ...node,
        selected: false,
      }))
    );
  }, []);

  // Detect if node membership (add/remove) or dimensions (populated by reactflow) have changed
  const srcNodeLayoutDepsHash = JSON.stringify([
    ...srcNodes.map(({data: {pmeiId}, height, width}) => ({pmeiId, height, width})),
    ...destNodes.map(({data: {pmeiId}, height, width}) => ({pmeiId, height, width})),
  ]);
  // Re-layout nodes when nodes are added or removed. The flow is:
  // 1) (render 1) Nodes are updated in state (added/removed/contents changed)
  // 2) (render 1) Reactflow queues node dimensions state updates via onNodesChange
  // 3) (render 2) This useLayoutEffect computes node positioning based on latest dimensions
  useLayoutEffect(
    () => {
      if (!areNodesInitialized) {
        return;
      }
      const positionsByPMEIId = getNodePositionsByPMEIId(srcNodes, destNodes, orderedDestPMEIsInColumns);
      function getNodesWithUpdatedPositions<N extends Node>(nodes: Array<N>): Array<N> {
        return produce(nodes, (nodes) => {
          for (const node of nodes) {
            if (positionsByPMEIId[node.data.pmeiId] != null) {
              node.position = positionsByPMEIId[node.data.pmeiId];
            }
          }
        });
      }
      setSrcNodes(getNodesWithUpdatedPositions);
      setDestNodes(getNodesWithUpdatedPositions);
    },
    // Can't use srcNodes or destNodes as dependencies because the reference changes every time state is set,
    // resulting in an infinite loop. Instead use srcNodeLayoutDepsHash to only detect changes we care about.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [areNodesInitialized, props.destinationPMEIs, props.sourcePMEIs, srcNodeLayoutDepsHash]
  );

  function onChangeLinkedSourcePMEIIds(linkedSourcePMEIIds: Array<string>): void {
    // Replace all edges for the selected destination nodes
    const uniqueSourcePMEIIds = Array.from(new Set(linkedSourcePMEIIds));
    const selectedDestPMEIIds = new Set(selectedDestNodes.map((node) => node.data.pmeiId));
    const withoutOldEdges = unitOpLinkEdges.filter(
      ({data}) => !selectedDestPMEIIds.has(assertNotNullAndReturn(data).destinationPMEIId)
    );
    const newEdges = Array.from(selectedDestPMEIIds).flatMap((destPMEIId) =>
      uniqueSourcePMEIIds.map((sourcePMEIId) =>
        makeUnitOpLinkEdge({
          sourcePMEIId,
          destinationPMEIId: destPMEIId,
        })
      )
    );
    const finalEdges = [...withoutOldEdges, ...newEdges];

    // Add any new source nodes for these linked PMEIs and clean up any orphaned source nodes without edges
    // We can't just compute all source nodes from finalEdges because we need to preserve other state (like
    // selection) in the source node objects.
    const withoutOrphanedNodes = srcNodes.filter((node) =>
      finalEdges.some((edge) => assertNotNullAndReturn(edge.data).sourcePMEIId === node.data.pmeiId)
    );
    const newNodes = uniqueSourcePMEIIds
      .filter((pmeiId) => !srcNodes.some((node) => node.data.pmeiId === pmeiId))
      .map((sourcePMEIId) =>
        makeSourcePMEINode(props.sourcePMEIs.find(({pmeiId}) => pmeiId === sourcePMEIId)!)
      );
    const finalSrcNodes = [...withoutOrphanedNodes, ...newNodes];

    // Update state and notify parent
    setSrcNodes(finalSrcNodes);
    setUnitOpLinkEdges(finalEdges);
    props.onChangeLinks(finalEdges.map(({data}) => assertNotNullAndReturn(data)));
  }

  return (
    <div
      className={css(styles.fullScreenContainer)}
      {...getTestProps({component: 'UnitOpLinkingFlowchartEditor', element: 'flowchartEditor'})}
    >
      <ReactFlow
        nodes={[...srcNodes, ...destNodes]}
        edges={unitOpLinkEdges.concat(intraRunMethodEdges)}
        nodeTypes={NODE_TYPES}
        // Handle non-sidebar interactions (eg. selection) and reactflow populating node dimensions
        onNodesChange={(nodeChanges) => {
          const [srcNodeChanges, destNodeChanges] = partition(
            // eslint-disable-next-line benchlint/no-in-operator
            nodeChanges.filter((nc) => 'id' in nc),
            (nc) => getNodeType(nc.id) === NodeType.SOURCE_PMEI
          );
          setSrcNodes((ns) => applyNodeChanges(srcNodeChanges, ns) as Array<SourcePMEINode>);
          setDestNodes((ns) => applyNodeChanges(destNodeChanges, ns) as Array<DestinationPMEINode>);
        }}
        onEdgesChange={onUnitOpLinkEdgesChange}
        onSelectionStart={() => {
          setIsSelecting(true);
        }}
        onSelectionEnd={() => {
          setIsSelecting(false);
        }}
        {...STATIC_PROPS}
      >
        {selectedDestNodes.length > 0 && (
          <FlowchartContextPanel
            panelSide={PanelSide.RIGHT}
            sidepanelName="Add Link"
            isOpen={isRightSidepanelOpen}
            onClose={handleRightSidepanelClose}
            size={SidepanelSize.SMALL}
            showExpandCollapseButtons={false}
          >
            <div className={css(isSelecting && styles.noUserSelect)}>
              <SourcePMEIPickerSidebar
                allPossibleSourcePMEIs={props.sourcePMEIs}
                selectedDestPMEIIds={selectedDestNodes.map(({data}) => data.pmeiId)}
                edges={unitOpLinkEdges.map(({data}) => data!)}
                onChangeLinkedSourcePMEIIds={onChangeLinkedSourcePMEIIds}
              />
            </div>
          </FlowchartContextPanel>
        )}
        <div className={css(styles.noUserSelect)}>
          <HorizontalControls />
        </div>
        <DefaultBackground />
      </ReactFlow>
    </div>
  );
}

function mapSourcesToSelectedDestinations(
  edges: Array<UnitOpLinkEdge>,
  selectedDestPMEIIds: Array<string>
): {[destPMEIId: string]: Set<string>} {
  const destinationsToSources: {[destPMEIId: string]: Set<string>} = {};
  for (const destPMEIId of selectedDestPMEIIds) {
    destinationsToSources[destPMEIId] = new Set<string>();
  }
  const selectedDestinationsSet = new Set(selectedDestPMEIIds);

  for (const edge of edges) {
    const {sourcePMEIId, destinationPMEIId} = edge;
    if (selectedDestinationsSet.has(destinationPMEIId)) {
      destinationsToSources[destinationPMEIId].add(sourcePMEIId);
    }
  }
  return destinationsToSources;
}

function SourcePMEIPickerSidebar(props: {
  selectedDestPMEIIds: Array<string>;
  allPossibleSourcePMEIs: Array<UnitOperationFields>;
  onChangeLinkedSourcePMEIIds: (linkedSourcePMEIIds: Array<string>) => void;
  edges: Array<UnitOpLinkEdge>;
}): JSX.Element {
  // PMEIs are grouped by procedureRunId and then by methodName
  const pmeisGroupedByProcedureIdAndMethod = new Map<string, Map<string, Array<UnitOperationFields>>>();
  const procedureRunDisplayIdMap = new Map<
    string,
    {procedureRunName: string; procedureRunDisplayId: string | null}
  >();

  props.allPossibleSourcePMEIs.forEach((pmei) => {
    assert(pmei.procedureRunName != null, 'unit operation instance expected to have a procedure run name');
    assert(pmei.procedureType != null, 'unit operation instance expected to have a procedure type');
    if (!pmeisGroupedByProcedureIdAndMethod.has(pmei.procedureRunId)) {
      pmeisGroupedByProcedureIdAndMethod.set(
        pmei.procedureRunId,
        new Map<string, Array<UnitOperationFields>>()
      );
    }
    const methodsMap = pmeisGroupedByProcedureIdAndMethod.get(pmei.procedureRunId)!;
    if (!methodsMap.has(pmei.methodName)) {
      methodsMap.set(pmei.methodName, []);
    }
    methodsMap.get(pmei.methodName)!.push(pmei);

    if (!procedureRunDisplayIdMap.has(pmei.procedureRunId)) {
      procedureRunDisplayIdMap.set(pmei.procedureRunId, {
        procedureRunName: pmei.procedureRunName,
        procedureRunDisplayId: pmei.procedureRunDisplayId,
      });
    }
  });

  const allSourceProcedureGroupItems: Array<TreeNode> = Array.from(procedureRunDisplayIdMap.entries()).map(
    ([procedureRunId, {procedureRunName, procedureRunDisplayId}]) => {
      const methodsForThisProcedure =
        pmeisGroupedByProcedureIdAndMethod.get(procedureRunId) ||
        new Map<string, Array<UnitOperationFields>>();
      const uniqueOrderedMethodNamesForProcedure = [
        ...new Set(
          props.allPossibleSourcePMEIs
            .filter((pmei) => pmei.procedureRunId === procedureRunId)
            .map(({methodName}) => methodName)
        ),
      ];

      const childrenMethodGroupItems: Array<TreeNode> = uniqueOrderedMethodNamesForProcedure.map(
        (methodName) => {
          const pmeisForThisMethod = methodsForThisProcedure.get(methodName) || [];

          const childrenPMEINodes: Array<TreeNode> = pmeisForThisMethod.map((pmeiDetails) => ({
            id: pmeiDetails.pmeiId,
            type: TreeNodeType.SELECTABLE_OPTION,
            label: `${pmeiDetails.conditionName} | Replicate ${pmeiDetails.replicateNumber}`,
          }));

          return {
            groupId: `methodgroup-${procedureRunId}-${methodName}`,
            label: getSidebarGroupLabel(methodName, null),
            type: TreeNodeType.GROUP,
            children: childrenPMEINodes,
          };
        }
      );

      return {
        groupId: `procedurerungroup-${procedureRunId}`,
        label: getSidebarGroupLabel(procedureRunName, procedureRunDisplayId),
        type: TreeNodeType.GROUP, // This is now a top-level group
        children: childrenMethodGroupItems,
      };
    }
  );

  const selectedOptionIdsByItemId = mapSourcesToSelectedDestinations(props.edges, props.selectedDestPMEIIds);
  const procedureType = props.allPossibleSourcePMEIs[0]?.procedureType;
  for (let i = 1; i < props.allPossibleSourcePMEIs.length; i++) {
    assert(
      props.allPossibleSourcePMEIs[i].procedureType === procedureType,
      'All procedure types must be the same.'
    );
  }

  return (
    <div
      className={css(styles.sidebar)}
      {...getTestProps({component: 'UnitOpLinkingFlowchartEditor', element: 'sourcePMEIPickerSidebar'})}
    >
      <BulkSelectOptionsForMultipleItemsTree
        selectedOptionIdsByItemId={selectedOptionIdsByItemId}
        onSelectOptions={(newSelectedOptionIdsByItemId) => {
          const pmeiIds = Object.values(newSelectedOptionIdsByItemId).flatMap((selectedOptionIds) =>
            Array.from(selectedOptionIds)
          );
          props.onChangeLinkedSourcePMEIIds(pmeiIds);
        }}
        optionsTree={allSourceProcedureGroupItems}
        optionsType={procedureType}
      />
    </div>
  );
}

function getSidebarGroupLabel(primaryText: string, secondaryText: string | null): JSX.Element {
  return (
    <div className={css(styles.procedureRunLabel)}>
      <span>{primaryText}</span>
      {secondaryText && <span className={css(styles.displayId)}>{secondaryText}</span>}
    </div>
  );
}

/** Compute positions for source and destination nodes. This supports nodes of varying dimensions.
 *
 * - Source nodes are laid out on the left, destination nodes on the right.
 * - Source nodes are laid out in a single vertical column: ordered by run, then method, then condition, then
 *   replicate.
 * - Destination nodes are laid out such that each method gets its own vertical column. In each method column,
 *   nodes are ordered by condition, then replicate.
 * - All source and destination node columns are vertically centered against each other.
 */
function getNodePositionsByPMEIId(
  srcNodes: Array<SourcePMEINode>,
  destNodes: Array<DestinationPMEINode>,
  orderedDestPMEIsInColumns: Array<Array<UnitOperationFields>>
): Record<string, XYPosition> {
  // Destination nodes are laid out in a grid with shape (condition replicate x method)
  const {positionsByPMEIId: destPositions, maxDestColumnHeight} = getNodePositionsByPMEIIdForProcedureRun(
    destNodes,
    orderedDestPMEIsInColumns,
    FIRST_DEST_COLUMN_X
  );

  // Source nodes are in a single column.
  const orderedSrcColumnNodes = srcNodes.toSorted((a, b) => sortComparePMEIs(a.data, b.data));

  // Figure out Y-offset for source nodes to center them with destination nodes.
  const srcColumnHeight = getHeightOfNodeColumn(orderedSrcColumnNodes);
  const srcNodesYOffset = (maxDestColumnHeight - srcColumnHeight) / 2;

  // Calculate positions for source nodes.
  const positionsByPMEIId: Record<string, XYPosition> = {...destPositions};
  const srcColumnX =
    FIRST_DEST_COLUMN_X -
    NODE_HORIZONTAL_GAP -
    Math.max(...orderedSrcColumnNodes.map((node) => node.width ?? 0));

  orderedSrcColumnNodes.forEach((node, i) => {
    if (i === 0) {
      positionsByPMEIId[node.data.pmeiId] = {x: srcColumnX, y: HIGHEST_DEST_NODE_Y + srcNodesYOffset};
    } else {
      const prevNode = orderedSrcColumnNodes[i - 1];
      positionsByPMEIId[node.data.pmeiId] = {
        x: FIRST_DEST_COLUMN_X - NODE_HORIZONTAL_GAP - (node.width ?? 0),
        // Node heights are not fixed, calc Y position based on existing node Y position and height.
        y: positionsByPMEIId[prevNode.data.pmeiId].y + (prevNode.height ?? 0) + NODE_VERTICAL_GAP,
      };
    }
  });

  return positionsByPMEIId;
}

function getNodeType(nodeId: string): NodeType {
  return nodeId.startsWith('source') ? NodeType.SOURCE_PMEI : NodeType.DESTINATION_PMEI;
}

function makeInitialSourcePMEINodes(
  sourcePMEIs: Array<UnitOperationFields>,
  links: Array<UnitOpLinkEdge>
): Array<SourcePMEINode> {
  const sourcesToRender = [...new Set(links.map(({sourcePMEIId}) => sourcePMEIId))];
  return sourcesToRender.map((sourcePMEIId) => {
    const pmeiData = sourcePMEIs.find(({pmeiId}) => pmeiId === sourcePMEIId);
    return makeSourcePMEINode(assertNotNullAndReturn(pmeiData));
  });
}
function makeInitialDestinationPMEINodes(
  orderedDestPMEIsInColumns: Array<Array<UnitOperationFields>>
): Array<DestinationPMEINode> {
  return orderedDestPMEIsInColumns.flatMap((pmeis, columnIndex) =>
    pmeis.map((pmei) => makeDestinationPMEINode(pmei, {selectable: columnIndex === 0}))
  );
}

export const TEST_EXPORTS = {
  SourcePMEIPickerSidebar,
  getNodePositionsByPMEIId,
};

const styles = StyleSheet.create({
  sidebar: {
    width: 316,
    height: '100%',
    background: WHITE,
  },
  fullScreenContainer: {
    margin: 0,
    width: '100%',
    height: '100%',
  },
  maxHeight: {
    maxHeight: '100%',
    overflowY: 'auto',
  },
  procedureRunLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    marginLeft: '4px',
  },
  displayId: {
    color: GRAY_SEMIDARK,
    marginRight: '12px',
  },
  noUserSelect: {
    userSelect: 'none',
  },
});
